import { call, put, select, takeLatest } from 'redux-saga/effects'
import { getPcrIdSelector } from '../../../home/selectors'
import { throwPcrException } from '../../../pcr/store/pcr.actions'
import { IPcr } from '../../../pcr/store/pcr.types'
import { getServiceIdSelector } from '../../../user/selectors'
import { PcrDocsService } from '../../../../services/pcrDocs.service'
import { performWithMutexHOG } from '../../../../services/mutexUtility.service'
import { IBloodProductAdministrationState } from '../store/bloodProductAdministration.types'
import { getBloodProductAdministrationSelector } from '../selectors/bloodProductAdministration.selector'
import { setAddToTimeLine } from '../store/bloodProductAdministration.actions'
import { BloodProductAdministrationConstants } from '../store/bloodProductAdministration.constants'
import { addBloodProcedureAdministrationItem } from '../../../timeline/store/timeline.actions'
import { handleNemsisCodeError, getUpdatedProcedureStateWithNemsisCode } from '../../../../utilities/nemsisCode.util'
import { getProcedureStateSelector } from '../../../../components/procedure/selectors'
import { INemsisSnomedLookup } from '../../../../components/procedure/store/procedure.types'

const processNemsisCode = (
  bloodProductAdministrationState: IBloodProductAdministrationState,
  nemsisCodeOptions: INemsisSnomedLookup[]
): IBloodProductAdministrationState => {
  try {
    if (!bloodProductAdministrationState.bloodProduct) {
      return getUpdatedProcedureStateWithNemsisCode({
        procedureState: bloodProductAdministrationState,
        actionField: () => null,
        filter1Field: () => null,
        filter2Field: () => null,
        filter3Field: () => null,
        nemsisCodeOptions,
        includeSnomedId: true,
      })
    } else {
      const { bloodProduct } = bloodProductAdministrationState
      const bloodProductNemsisCode = bloodProduct?.nemsisCode
      const bloodProductSnomedId =
        nemsisCodeOptions.find(
          (option) =>
            option.code === bloodProductNemsisCode && option.procedure === bloodProductAdministrationState.type
        )?.snomedId ?? null

      return {
        ...bloodProductAdministrationState,
        nemsisCode: (bloodProductNemsisCode as number) ?? null,
        snomedId: bloodProductSnomedId,
      }
    }
  } catch (error) {
    return handleNemsisCodeError(error, bloodProductAdministrationState)
  }
}

export function* saveBloodProductAdministration() {
  let bloodProductAdministrationState: IBloodProductAdministrationState = yield select(
    getBloodProductAdministrationSelector
  )
  const serviceId: string = yield select(getServiceIdSelector)
  const pcrDocsService = new PcrDocsService(serviceId)
  const pcrID: string = yield select(getPcrIdSelector)
  const { nemsisCodeOptions } = yield select(getProcedureStateSelector)

  if (!pcrID) {
    return
  }

  bloodProductAdministrationState = processNemsisCode(bloodProductAdministrationState, nemsisCodeOptions)

  const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrID)

  try {
    const existingBloodProductAdministrationList = pcrDoc.patient?.bloodProductAdministrationStateList || []
    const updatedList = [...existingBloodProductAdministrationList]
    const index = updatedList.findIndex((item) => item.key === bloodProductAdministrationState.key)

    if (index !== -1) {
      updatedList[index] = bloodProductAdministrationState
    } else {
      updatedList.push(bloodProductAdministrationState)
    }

    const pcrDocUpdated: IPcr = {
      ...pcrDoc,
      patient: {
        ...pcrDoc.patient,
        bloodProductAdministrationStateList: updatedList,
      },
    }

    yield call([pcrDocsService, pcrDocsService.savePCR], pcrDocUpdated)
    yield put(setAddToTimeLine(true))
    yield put(addBloodProcedureAdministrationItem())
  } catch (e) {
    yield put(throwPcrException(`saveBloodProductAdministrationSaga() error: ${e}`))
  }
}

export function* saveBloodProductAdministrationSaga() {
  yield takeLatest(
    [
      BloodProductAdministrationConstants.SAVE_BLOOD_PRODUCT_ADMINISTRATION,
      BloodProductAdministrationConstants.SET_DATE,
      BloodProductAdministrationConstants.SET_TIME,
      BloodProductAdministrationConstants.SET_RESPONSE,
      BloodProductAdministrationConstants.SET_RESPONSE_PNNV,
      BloodProductAdministrationConstants.SET_DATE_TIME,
      BloodProductAdministrationConstants.SET_ASSESSED_BY,
      BloodProductAdministrationConstants.SET_PROCEDURE_COMPLICATIONS,
      BloodProductAdministrationConstants.SET_ROLE_PERFORMING,
      BloodProductAdministrationConstants.SET_ROLE_PERFORMING_PNNV,
      BloodProductAdministrationConstants.SET_PROCEDURE_PERFORMED_BY_ANOTHER_UNIT,
      BloodProductAdministrationConstants.SET_PROCEDURE_PERFORMED_BY_ANOTHER_UNIT_PNNV,
      BloodProductAdministrationConstants.SET_TRANSFUSION_CONSENT_DATE,
      BloodProductAdministrationConstants.SET_TRANSFUSION_CONSENT_TIME,
      BloodProductAdministrationConstants.SET_TRANSFUSION_CONSENT_DATE_TIME,
      BloodProductAdministrationConstants.SET_UNIT_EXPIRY_DATE,
      BloodProductAdministrationConstants.SET_UNIT_EXPIRY_TIME,
      BloodProductAdministrationConstants.SET_UNIT_EXPIRY_DATE_TIME,
      BloodProductAdministrationConstants.SET_TRANSFUSION_START_DATE,
      BloodProductAdministrationConstants.SET_TRANSFUSION_START_TIME,
      BloodProductAdministrationConstants.SET_TRANSFUSION_START_DATE_TIME,
      BloodProductAdministrationConstants.SET_TRANSFUSION_END_DATE,
      BloodProductAdministrationConstants.SET_TRANSFUSION_END_TIME,
      BloodProductAdministrationConstants.SET_TRANSFUSION_END_DATE_TIME,
      BloodProductAdministrationConstants.SET_PTA,
      BloodProductAdministrationConstants.SET_WARMER_USED,
      BloodProductAdministrationConstants.SET_UNIT_COMPLETED,
      BloodProductAdministrationConstants.SET_ATTEMPTS,
    ],
    performWithMutexHOG,
    saveBloodProductAdministration
  )
}
